import React, { useState, useEffect, useCallback, memo } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Menu,
  X,
  LayoutDashboard,
  BookText,
  Video,
  FileCheck,
  CalendarDays,
  GraduationCap,
  FileArchive,
  Users,
  LogOut,
  ChevronRight,
  FileText,
  FileQuestion,
  Folder,
  Bell,
  Moon,
  Sun
} from "lucide-react";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { ref, get, update } from "firebase/database";
import logo from "./assets/x_math_logo.png";
import syltixLogo from "./assets/syltix.png";
import { formatCourseId } from "./utils/formatCourse";
import "./studentTheme.css";

const THEME_KEY = "xmaths-student-theme";

const FILE_PATHS = [
  "/dashboard/markschemes",
  "/dashboard/extra-questions",
  "/dashboard/pdfs",
  "/dashboard/pastpapers"
];

const PAGE_TITLES = [
  ["/dashboard/extra-questions", "Extra Questions"],
  ["/dashboard/markschemes", "Mark Schemes"],
  ["/dashboard/pastpapers", "Past Papers"],
  ["/dashboard/online-meetings", "Online Meetings"],
  ["/dashboard/announcements", "Announcements"],
  ["/dashboard/homework_track", "Homework"],
  ["/dashboard/homeworks", "Homework"],
  ["/dashboard/chapters", "Progress"],
  ["/dashboard/profile", "Profile"],
  ["/dashboard/videos", "Videos"],
  ["/dashboard/exams", "Exam Grades"],
  ["/dashboard/pdfs", "PDFs"]
];

function isPathActive(pathname, path) {
  if (path === "/dashboard") return pathname === "/dashboard";
  return pathname === path || pathname.startsWith(`${path}/`);
}

function getPageTitle(pathname) {
  const match = PAGE_TITLES.find(([path]) => isPathActive(pathname, path));
  return match ? match[1] : "Dashboard";
}

const Layout = memo(() => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filesMenuOpen, setFilesMenuOpen] = useState(false);
  const [username, setUsername] = useState("Loading...");
  const [studentId, setStudentId] = useState("");
  const [initials, setInitials] = useState("");
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || "dark");
  const light = theme === "light";
  const navigate = useNavigate();
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  const fetchUserData = useCallback(async (user) => {
    if (!user) {
      setUsername("Not Logged In");
      setStudentId("");
      setInitials("");
      return;
    }

    try {
      const email = user.email;
      let foundUser = null;
      let courseName = null;

      const usersRef = ref(db, "users");
      const usersSnapshot = await get(usersRef);

      if (usersSnapshot.exists()) {
        const usersData = usersSnapshot.val();
        foundUser = Object.values(usersData).find(
          (u) => u.email === email
        );
      }

      if (!foundUser) {
        const coursesRef = ref(db, "courses");
        const coursesSnapshot = await get(coursesRef);

        if (coursesSnapshot.exists()) {
          const courses = coursesSnapshot.val();

          for (const [courseId, courseData] of Object.entries(courses)) {
            if (courseData.students) {
              const student = Object.values(courseData.students).find(
                (s) => s.email === email
              );
              if (student) {
                foundUser = student;
                courseName = courseId;
                break;
              }
            }
          }
        }
      }

      if (foundUser) {
        const fullName = (foundUser.name || "Student").trim();
        const nameParts = fullName.split(/\s+/).filter(Boolean);
        const firstName = nameParts[0] || "Student";
        const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";

        setUsername(firstName);
        setStudentId(foundUser.studentId || "");
        setInitials(`${firstName.charAt(0)}${lastName ? lastName.charAt(0) : ""}`.toUpperCase());
        setEnrolledCourse(courseName);
      } else {
        setUsername("Student");
        setStudentId("");
        setInitials("S");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUsername("Student");
      setStudentId("");
      setInitials("S");
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, fetchUserData);
    return () => unsubscribe();
  }, [fetchUserData]);

  useEffect(() => {
    if (FILE_PATHS.some((path) => isPathActive(location.pathname, path))) {
      setFilesMenuOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const handleLogout = useCallback(async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const coursesRef = ref(db, "courses");
        const coursesSnapshot = await get(coursesRef);

        if (coursesSnapshot.exists()) {
          const courses = coursesSnapshot.val();
          const updatePromises = [];

          for (const [courseId, courseData] of Object.entries(courses)) {
            if (courseData.students) {
              const student = Object.values(courseData.students).find(
                (s) => s.email === currentUser.email
              );
              if (student) {
                const studentRef = ref(db, `courses/${courseId}/students/${student.studentId}`);
                updatePromises.push(update(studentRef, { isLoggedIn: false }));
              }
            }
          }

          if (updatePromises.length > 0) {
            await Promise.all(updatePromises);
          }
        }
      }

      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  }, [navigate]);

  const navItems = [
    { name: "Dashboard", path: "/dashboard", icon: <LayoutDashboard size={20} /> },
    { name: "Progress", path: "/dashboard/chapters", icon: <BookText size={20} /> },
    { name: "Videos", path: "/dashboard/videos", icon: <Video size={20} /> },
    {
      name: "Files",
      icon: <Folder size={20} />,
      subItems: [
        { name: "Mark Schemes", path: "/dashboard/markschemes", icon: <FileCheck size={18} /> },
        { name: "Extra Questions", path: "/dashboard/extra-questions", icon: <FileQuestion size={18} /> },
        { name: "PDFs", path: "/dashboard/pdfs", icon: <FileText size={18} /> },
        { name: "Past Papers", path: "/dashboard/pastpapers", icon: <FileArchive size={18} /> },
      ]
    },
    { name: "Homework", path: "/dashboard/homeworks", icon: <CalendarDays size={20} /> },
    { name: "Exam Grades", path: "/dashboard/exams", icon: <GraduationCap size={20} /> },
    { name: "Online Meetings", path: "/dashboard/online-meetings", icon: <Users size={20} /> },
    { name: "Announcements", path: "/dashboard/announcements", icon: <Bell size={20} /> },
  ];

  const linkClass = (active) =>
    `flex items-center w-full gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
      active
        ? "bg-blue-600 text-white shadow-md shadow-blue-950/40"
        : light
          ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          : "text-gray-300 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <div className={`student-shell ${light ? "light" : "dark"} flex min-h-screen ${light ? "bg-slate-50 text-slate-900" : "bg-gray-950 text-white"}`}>
      <div
        className={`fixed inset-0 z-40 bg-black/60 transition-opacity sm:hidden ${
          sidebarOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setSidebarOpen(false)}
        aria-hidden={!sidebarOpen}
      />

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r transition-transform duration-300 ${
          light ? "border-slate-200 bg-white" : "border-white/10 bg-gray-900"
        } ${sidebarOpen ? "translate-x-0" : "invisible -translate-x-full sm:visible sm:translate-x-0"}`}
      >
        <div className="shrink-0 border-b border-white/10 bg-gradient-to-br from-blue-600 via-blue-700 to-sky-800 px-4 py-4">
          <div className="flex items-center gap-2">
            <Link
              to="/dashboard"
              className="flex min-w-0 flex-1 items-center justify-center rounded-2xl bg-white px-3 py-2.5 shadow-sm"
              onClick={() => setSidebarOpen(false)}
            >
              <img src={logo} alt="X-Maths" className="h-14 w-auto max-w-full object-contain" />
            </Link>
            <button
              type="button"
              className="shrink-0 rounded-xl bg-white/15 p-2.5 text-white hover:bg-white/25 sm:hidden"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          </div>
          {enrolledCourse && (
            <div className="mt-3 flex justify-center px-2">
              <span className="inline-flex max-w-full items-center rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white ring-1 ring-white/25">
                <span className="truncate">{formatCourseId(enrolledCourse)}</span>
              </span>
            </div>
          )}
        </div>

        <nav className="custom-scrollbar flex-1 overflow-y-auto px-3 py-4" aria-label="Student">
          <ul className="space-y-1">
            {navItems.map((item) => {
              if (item.subItems) {
                const childActive = item.subItems.some((subItem) => isPathActive(location.pathname, subItem.path));
                return (
                  <li key={item.name}>
                    <button
                      type="button"
                      onClick={() => setFilesMenuOpen((open) => !open)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                        childActive
                          ? light ? "bg-slate-100 text-slate-900" : "bg-white/5 text-white"
                          : light
                            ? "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                            : "text-gray-300 hover:bg-white/5 hover:text-white"
                      }`}
                      aria-expanded={filesMenuOpen}
                    >
                      <span className={childActive || filesMenuOpen ? (light ? "text-blue-600" : "text-blue-300") : (light ? "text-slate-400" : "text-gray-400")}>{item.icon}</span>
                      <span className="font-medium">{item.name}</span>
                      <ChevronRight
                        size={16}
                        className={`ml-auto transition-transform duration-200 ${filesMenuOpen ? "rotate-90" : ""}`}
                      />
                    </button>
                    {filesMenuOpen && (
                      <ul className={`ml-4 mt-1 space-y-1 border-l pl-2 ${light ? "border-slate-200" : "border-white/10"}`}>
                        {item.subItems.map((subItem) => {
                          const active = isPathActive(location.pathname, subItem.path);
                          return (
                            <li key={subItem.name}>
                              <Link
                                to={subItem.path}
                                className={linkClass(active)}
                                aria-current={active ? "page" : undefined}
                                onClick={() => setSidebarOpen(false)}
                              >
                                <span className={active ? "text-white" : light ? "text-slate-400" : "text-gray-400"}>{subItem.icon}</span>
                                <span className="text-sm font-medium">{subItem.name}</span>
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }

              const active = isPathActive(location.pathname, item.path);
              return (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className={linkClass(active)}
                    aria-current={active ? "page" : undefined}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className={active ? "text-white" : light ? "text-slate-400" : "text-gray-400"}>{item.icon}</span>
                    <span className="font-medium">{item.name}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={`shrink-0 space-y-3 border-t p-3 ${light ? "border-slate-200" : "border-white/10"}`}>
          <button
            type="button"
            onClick={handleLogout}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 ${
              light ? "text-red-600 hover:bg-red-50" : "text-red-300 hover:bg-red-500/10 hover:text-red-200"
            }`}
          >
            <LogOut size={20} />
            <span className="font-medium">Sign Out</span>
          </button>
          <div className="flex items-center justify-center gap-2 pb-1">
            <p className="font-mono text-[11px] text-gray-500">Powered by</p>
            <a
              href="https://syltix.dev"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 transition-opacity hover:opacity-80"
            >
              <img
                src={syltixLogo}
                alt=""
                className="h-4 w-auto"
                style={light ? undefined : { filter: "invert(1) brightness(2)" }}
              />
              <span className="text-xs font-semibold text-gray-400">Syltix</span>
            </a>
          </div>
        </div>
      </aside>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.12);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.22);
        }
      `}</style>

      <div className="flex min-w-0 flex-1 flex-col sm:ml-72">
        <header className={`sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b px-4 backdrop-blur-md sm:px-6 ${
          light ? "border-slate-200 bg-white/90" : "border-white/10 bg-gray-950/90"
        }`}>
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              className={`rounded-lg p-2 sm:hidden ${light ? "text-slate-600 hover:bg-slate-100" : "text-gray-300 hover:bg-white/5 hover:text-white"}`}
              onClick={() => setSidebarOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>
            <Link
              to="/dashboard"
              className={`sm:hidden flex items-center rounded-xl px-2 py-1 ${light ? "" : "bg-white"}`}
              onClick={() => setSidebarOpen(false)}
            >
              <img src={logo} alt="X-Maths" className="h-8 w-auto max-w-[7.5rem] object-contain" />
            </Link>
            <div className="hidden min-w-0 sm:block">
              <p className={`truncate text-base font-semibold ${light ? "text-slate-900" : "text-white"}`}>{pageTitle}</p>
              {enrolledCourse && (
                <p className={`truncate text-xs ${light ? "text-slate-500" : "text-gray-400"}`}>{formatCourseId(enrolledCourse)}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={() => setTheme(light ? "dark" : "light")}
            className={`rounded-lg p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
              light ? "text-slate-600 hover:bg-slate-100" : "text-gray-300 hover:bg-white/10 hover:text-white"
            }`}
            aria-label={light ? "Switch to dark theme" : "Switch to light theme"}
            title={light ? "Switch to dark theme" : "Switch to light theme"}
          >
            {light ? <Moon size={18} /> : <Sun size={18} />}
          </button>

          <button
            type="button"
            className={`flex min-w-0 items-center gap-2.5 rounded-full border py-1 pl-1 pr-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 sm:pr-3 ${
              light
                ? "border-slate-200 bg-white hover:border-blue-300"
                : "border-white/10 bg-white/5 hover:border-blue-400/40"
            }`}
            onClick={() => navigate("/dashboard/profile")}
            aria-label="Open profile"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-sky-500 text-sm font-semibold tracking-wide text-white">
              {initials || "S"}
            </span>
            <span className="min-w-0 max-w-[9.5rem] sm:max-w-[12rem]">
              <span className={`block truncate text-sm font-semibold leading-tight ${light ? "text-slate-900" : "text-white"}`}>{username}</span>
              {studentId && (
                <span className={`mt-1 block w-fit max-w-full truncate rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide ${
                  light ? "bg-blue-50 text-blue-700" : "bg-blue-500/20 text-blue-100"
                }`}>
                  {studentId}
                </span>
              )}
            </span>
          </button>
          </div>
        </header>

        <main className="dash-main flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
});

export default Layout;
